// =============================================================================
// MeuDriver — Cartão de identidade do instrutor
// -----------------------------------------------------------------------------
// Opt-in para aparecer no filtro "Apenas instrutoras mulheres". Declaração
// explícita do próprio instrutor — nunca inferimos pelo nome.
// =============================================================================

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Loader2, UserCog } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

export function InstructorIdentityCard() {
  const { user } = useAuth()
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    let active = true
    ;(async () => {
      const { data } = await supabase
        .from('instructors')
        .select('is_woman_instructor')
        .eq('profile_id', user.id)
        .maybeSingle()
      if (active) {
        setEnabled((data as { is_woman_instructor?: boolean } | null)?.is_woman_instructor === true)
        setLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [user?.id])

  async function toggle(value: boolean) {
    if (!user?.id) return
    setSaving(true)
    setEnabled(value)
    const { error } = await supabase
      .from('instructors')
      .update({ is_woman_instructor: value })
      .eq('profile_id', user.id)
    setSaving(false)
    if (error) {
      toast.error('Erro ao salvar preferência.')
      setEnabled(!value)
    } else {
      toast.success(
        value
          ? 'Você aparecerá no filtro "Apenas instrutoras mulheres".'
          : 'Removido do filtro.',
      )
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <UserCog className="h-4 w-4 text-pink-600" />
          Identidade
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-800">
              Aparecer no filtro "Instrutoras mulheres"
            </p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Algumas alunas se sentem mais confortáveis aprendendo com instrutoras.
              Marque esta opção para aparecer quando elas filtrarem por "Apenas instrutoras
              mulheres". Esta declaração é voluntária.
            </p>
          </div>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0 mt-1" />
          ) : (
            <Switch
              checked={enabled}
              onCheckedChange={toggle}
              disabled={saving}
              aria-label="Instrutora mulher"
            />
          )}
        </div>
      </CardContent>
    </Card>
  )
}
